/* eslint-disable max-lines-per-function, complexity */
import type {
  ReleasedRecallDetail,
  RouteRecallDetail,
  RouteTrackerItem
} from '@/types/routeTracking';

// Shape of a single resolved-recall entry shown in the Status Updates surface.
// Carries enough context (technicianId + routeSequence) to group by tech and
// to deep-link into the per-tech Daily Route Summary.
export interface UpdateHistoryEntry {
  assignmentId: string;
  recall: RouteRecallDetail;
  status: 'completed' | 'vehicle_sold' | 'not_available' | 'incomplete' | 'other';
  otherReason?: string;
  updatedBy: string;
  updatedAt: string; // ISO
  routeDate: string;
  routeId: string;
  technicianId: string;
  technicianName: string;
  routeSequence: number;
}

/**
 * Rows that offer the customer contact: the first row of each household in a status
 * group. One reveal per household avoids repeated buttons (and repeated access logs)
 * when several of its vehicles were serviced.
 */
export function firstRowPerHousehold(entries: UpdateHistoryEntry[]): Set<UpdateHistoryEntry> {
  const seen = new Set<string>();
  const rows = new Set<UpdateHistoryEntry>();
  for (const entry of entries) {
    const contactId = entry.recall.stopKind === 'customer' ? entry.recall.contactId : null;
    if (contactId && !seen.has(contactId)) {
      seen.add(contactId);
      rows.add(entry);
    }
  }
  return rows;
}

// Manual-stop and customer-stop fields a released record carries (absent on older ones).
function releasedStopDetail(
  released: ReleasedRecallDetail
): Pick<
  RouteRecallDetail,
  'stopType' | 'manualStopReason' | 'stopKind' | 'contactId' | 'addressLine2' | 'dispatcherNote'
> {
  return {
    ...(released.stopType && { stopType: released.stopType }),
    ...(released.manualStopReason && { manualStopReason: released.manualStopReason }),
    ...(released.stopKind && { stopKind: released.stopKind }),
    ...(released.contactId && { contactId: released.contactId }),
    ...(released.addressLine2 && { addressLine2: released.addressLine2 }),
    ...(released.dispatcherNote && { dispatcherNote: released.dispatcherNote })
  };
}

// Build a deduplicated, time-sorted list of resolved recalls across all routes
// for the selected date. Source of truth for the Status Updates section.
//
// Entries come from two places:
//   1. route.recalls — assignments still in technician_assignments (completed, vehicle_sold,
//      close-out other).
//   2. route.releasedRecalls — JSONB-cached records from before the assignment was deleted
//      (release: not_available, incomplete, release other).
//
// Dedup key is `${vin}|${campaignNumber}` — keeping the most recent entry. This
// prevents "Completed" and "Incomplete" showing simultaneously for the same stop.
export function buildUpdateHistory(routes: RouteTrackerItem[]): UpdateHistoryEntry[] {
  const entries: UpdateHistoryEntry[] = [];

  routes.forEach(route => {
    // Index releases by recall key for fast lookup against route.recalls.
    const releasedMap = new Map<string, ReleasedRecallDetail>();
    (route.releasedRecalls ?? []).forEach(released => {
      releasedMap.set(`${released.vin}|${released.campaignNumber}`, released);
    });

    // Pass 1: process recalls that still live in technician_assignments.
    route.recalls.forEach(recall => {
      const released = releasedMap.get(`${recall.vin}|${recall.campaignNumber}`);

      let finalStatus: UpdateHistoryEntry['status'] = 'completed';
      if (released) {
        const raw = released.releaseStatus as string;
        finalStatus = raw === 'not_there' ? 'not_available' : released.releaseStatus;
      } else if (recall.status === 'completed') {
        finalStatus = 'completed';
      } else if (recall.status === 'cancelled' || recall.status === 'in_progress') {
        if (recall.releaseStatus) {
          finalStatus =
            recall.releaseStatus === 'not_there' ? 'not_available' : recall.releaseStatus;
        } else if (recall.status === 'in_progress') {
          finalStatus = 'incomplete';
        }
      }

      const isResolved =
        recall.status === 'completed' ||
        recall.status === 'cancelled' ||
        recall.status === 'in_progress' ||
        !!released;
      if (!isResolved) return;

      const updatedAt =
        released?.releasedAt || recall.completedAt || recall.assignedAt || new Date().toISOString();
      const releaseReason = released?.releaseReason || recall.releaseReason;

      entries.push({
        assignmentId: recall.assignmentId,
        recall,
        status: finalStatus,
        ...(releaseReason && { otherReason: releaseReason }),
        updatedBy:
          released?.releasedByUserName ||
          recall.completedByUserName ||
          route.technicianName ||
          'System',
        updatedAt,
        routeDate: route.routeDate,
        routeId: route.routeId,
        technicianId: route.technicianId,
        technicianName: route.technicianName,
        routeSequence: route.routeSequence || 1
      });
    });

    // Pass 2: released recalls whose underlying assignment was deleted before the
    // route JSONB was rewritten — these have no matching route.recalls entry.
    (route.releasedRecalls ?? []).forEach(released => {
      const key = `${released.vin}|${released.campaignNumber}`;
      const alreadyProcessed = route.recalls.some(r => `${r.vin}|${r.campaignNumber}` === key);
      if (alreadyProcessed) return;

      const raw = released.releaseStatus as string;
      const finalStatus: UpdateHistoryEntry['status'] =
        raw === 'not_there' ? 'not_available' : released.releaseStatus;

      // Reverted: same-dealer address fallback was suspected of causing a regression
      // in the Status Updates section. Restore the original minimal proxy so released
      // entries with deleted assignments behave exactly as they did before.
      const recallProxy: RouteRecallDetail = {
        assignmentId: released.assignmentId,
        vin: released.vin,
        vehicleYear: released.vehicleYear,
        vehicleMake: released.vehicleMake,
        vehicleModel: released.vehicleModel,
        campaignNumber: released.campaignNumber || '',
        recallTitle: '',
        dealerName: released.dealerName,
        dealerCity: released.dealerCity || '',
        dealerRegion: released.dealerRegion || '',
        status: 'cancelled',
        assignedAt: route.confirmedAt,
        releaseStatus: released.releaseStatus,
        releaseReason: released.releaseReason,
        // Stop kind/ids from the released record itself (never inferred from neighbours,
        // per the revert above); older records lack them and stay plain entries.
        ...releasedStopDetail(released)
      };

      entries.push({
        assignmentId: released.assignmentId,
        recall: recallProxy,
        status: finalStatus,
        ...(released.releaseReason && { otherReason: released.releaseReason }),
        updatedBy: released.releasedByUserName || route.technicianName,
        updatedAt: released.releasedAt,
        routeDate: route.routeDate,
        routeId: route.routeId,
        technicianId: route.technicianId,
        technicianName: route.technicianName,
        routeSequence: route.routeSequence || 1
      });
    });
  });

  // Dedup by vin|campaign, keep the most recent.
  const dedup = new Map<string, UpdateHistoryEntry>();
  entries.forEach(entry => {
    const key = `${entry.recall.vin}|${entry.recall.campaignNumber}`;
    const existing = dedup.get(key);
    if (!existing || new Date(entry.updatedAt) > new Date(existing.updatedAt)) {
      dedup.set(key, entry);
    }
  });

  // Most-recent first.
  return Array.from(dedup.values()).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

// Visual treatment for each resolution status. Centralized so the Status Updates
// section, the Daily Summary, and any future surfaces stay consistent.
//
// • colorClass  — text color for status labels (e.g. group header titles)
// • borderClass — subtle 3px left border on row-level rendering
// • dotClass    — solid background for the count chips on the TechDayCard header,
//                 so dispatchers see per-status breakdowns at a glance even with
//                 the card collapsed. Same hue as borderClass (consistent palette).
// Color always pairs with a label or tooltip — accessibility-preserved.
export const STATUS_META: Record<
  UpdateHistoryEntry['status'],
  { label: string; colorClass: string; ring: string; borderClass: string; dotClass: string }
> = {
  completed: {
    label: 'Completed',
    colorClass: 'text-green-700 dark:text-green-400',
    ring: 'ring-green-200 dark:ring-green-900/40',
    borderClass: 'border-l-green-400 dark:border-l-green-600',
    dotClass: 'bg-green-500 dark:bg-green-400'
  },
  incomplete: {
    label: 'Incomplete',
    colorClass: 'text-blue-700 dark:text-blue-400',
    ring: 'ring-blue-200 dark:ring-blue-900/40',
    borderClass: 'border-l-blue-400 dark:border-l-blue-600',
    dotClass: 'bg-blue-500 dark:bg-blue-400'
  },
  vehicle_sold: {
    label: 'Vehicle Sold',
    colorClass: 'text-red-700 dark:text-red-400',
    ring: 'ring-red-200 dark:ring-red-900/40',
    borderClass: 'border-l-red-400 dark:border-l-red-600',
    dotClass: 'bg-red-500 dark:bg-red-400'
  },
  not_available: {
    label: 'Not Available',
    colorClass: 'text-orange-700 dark:text-orange-400',
    ring: 'ring-orange-200 dark:ring-orange-900/40',
    borderClass: 'border-l-orange-400 dark:border-l-orange-600',
    dotClass: 'bg-orange-500 dark:bg-orange-400'
  },
  other: {
    label: 'Other',
    colorClass: 'text-gray-700 dark:text-gray-400',
    ring: 'ring-gray-200 dark:ring-gray-700',
    borderClass: 'border-l-gray-300 dark:border-l-gray-600',
    dotClass: 'bg-gray-400 dark:bg-gray-500'
  }
};

// Stable display order for status groups inside a TechDayCard.
export const STATUS_ORDER: UpdateHistoryEntry['status'][] = [
  'completed',
  'incomplete',
  'vehicle_sold',
  'not_available',
  'other'
];
